/* eslint-disable no-mixed-spaces-and-tabs */
import { call, fork, put, takeEvery, select } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import { notificationInit } from '../../../../modules/notifications/actions';
import { FORM_NAME, FORM_FIELDS, REQUEST_ERROR_MESSAGE } from './constants';
import { DISMISS_TIME } from '../../../../api/constants';
import { errorHandler } from '../../../../api/utils';
import {
	GET_AUTOPARKS,
	GET_CONFIGS,
	GET_MODES,
	GET_ORDER_GROUP_INFO,
	GET_TARIFF_TYPES,
	GET_TRANSPORT_TYPES,
	PLANNING,
	setAutoparksAction,
	setConfigsAction,
	setIsAutoparksLoadingAction,
	setIsConfigsLoadingAction,
	setIsModesLoadingAction,
	setIsPlanningLoadingAction,
	setIsTariffTypesLoadingAction,
	setIsTransportTypesLoadingAction,
	setModesAction,
	setOrderGroupInfoAction,
	setOrderGroupInfoLoadingAction,
	setTariffTypesAction,
	setTransportTypesAction,
} from './actions';
import { getGroupsListAction, getCollectionGroupListAction, getGroupsWithWaysListAction } from '../../actions';
import { getDataForCreateOrUpdateSelector, getFormIsInvalidSelector } from './selectors';
import { getGroupOpenSelector } from '../../selectors';
import { transformToValidationErrors } from './utils';
import {
	getAutoparksRequest,
	getAutoParkTransportTariffTypesRequest,
	getModesDictionaryRequest,
	getOrderGroupInfoRequest,
	getVeerouteConfigsRequest,
	getVeerouteTransportTypesRequest,
	planningRequest,
} from '../../../../api/requests';

function* getOrderGroupInfoSaga({ payload: { id } }) {
	try {
		yield put(setOrderGroupInfoLoadingAction(true));
		const { data, message, toast } = yield call(getOrderGroupInfoRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setOrderGroupInfoAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (e) {
		yield fork(errorHandler, e);
	} finally {
		yield put(setOrderGroupInfoLoadingAction(false));
	}
}

export function* planningSaga({ payload: { searchQuery, group, process, callback } }) {
	try {
		yield put(setIsPlanningLoadingAction(true));

		const groupOpen = yield select(getGroupOpenSelector());

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForCreateOrUpdateSelector());

		const id = process ? process.group.id : group.id;

		const process_id = process ? process.id : null;

		const { data, message, toast, errors } = yield call(planningRequest, id, process_id, {
			...dataForSave,
		});

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(getGroupsListAction({ searchQuery, noLoading: true }));
			yield put(getGroupsWithWaysListAction({ searchQuery, noLoading: true }));

			if (group && groupOpen && groupOpen.id === group.id) {
				yield put(getCollectionGroupListAction({ row: groupOpen, searchQuery }));
			}

			callback();
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...FORM_FIELDS));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsPlanningLoadingAction(false));
	}
}

export function* getConfigsSaga() {
	try {
		yield put(setIsConfigsLoadingAction(true));

		const { data, message, toast } = yield call(getVeerouteConfigsRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setConfigsAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsConfigsLoadingAction(false));
	}
}

export function* getTransportTypesSaga() {
	try {
		yield put(setIsTransportTypesLoadingAction(true));

		const { data, message, toast } = yield call(
			getVeerouteTransportTypesRequest,
			'?order.column=id&order.direct=asc&receive=all',
		);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setTransportTypesAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTransportTypesLoadingAction(false));
	}
}

export function* getModesSaga() {
	try {
		yield put(setIsModesLoadingAction(true));

		const { data, message, toast } = yield call(getModesDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setModesAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsModesLoadingAction(false));
	}
}

export function* getAutoparksSaga() {
	try {
		yield put(setIsAutoparksLoadingAction(true));
		const { data, message, toast } = yield call(getAutoparksRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setAutoparksAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsAutoparksLoadingAction(false));
	}
}

export function* getTariffTypesSaga() {
	try {
		yield put(setIsTariffTypesLoadingAction(true));
		const { data, message, toast } = yield call(getAutoParkTransportTariffTypesRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setTariffTypesAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTariffTypesLoadingAction(false));
	}
}

export default function* orderGroupsChangeGroupContainerSaga() {
	yield takeEvery(GET_ORDER_GROUP_INFO, getOrderGroupInfoSaga);
	yield takeEvery(PLANNING, planningSaga);
	yield takeEvery(GET_CONFIGS, getConfigsSaga);
	yield takeEvery(GET_TRANSPORT_TYPES, getTransportTypesSaga);
	yield takeEvery(GET_MODES, getModesSaga);
	yield takeEvery(GET_TARIFF_TYPES, getTariffTypesSaga);
	yield takeEvery(GET_AUTOPARKS, getAutoparksSaga);
}
